<template>
  <div class="ocr-report-map-panel">
    <v-alert v-if="!filename || tableIndex == null" type="info" variant="tonal" density="compact">
      Select a report and table from the sidebar.
    </v-alert>
    <template v-else>
      <div class="ocr-report-map-toolbar">
        <div class="ocr-map-toolbar-group">
          <span class="ocr-map-toolbar-label">Page</span>
          <v-switch
            v-model="viewReportPage"
            density="compact"
            hide-details
            color="primary"
            label="View report page"
            class="ocr-map-view-report-switch"
            :class="{ 'ocr-map-view-report-switch--on': viewReportPage }"
          />
          <v-checkbox
            v-model="manualSelect"
            density="compact"
            hide-details
            :disabled="!viewReportPage"
            label="Manually select table on image"
            class="ocr-manual-select-toggle"
          />
          <v-btn size="small" variant="outlined" class="ocr-toolbar-btn" :disabled="!viewReportPage" @click="openInvert">
            Invert Page
          </v-btn>
          <v-btn size="small" variant="outlined" class="ocr-toolbar-btn" :disabled="!viewReportPage" :loading="resettingOrientation" @click="resetOrientation">
            Reset Orientation
          </v-btn>
        </div>
        <div class="ocr-map-toolbar-group">
          <span class="ocr-map-toolbar-label">Type</span>
          <div class="ocr-field ocr-report-map-type-select">
            <!-- <label class="profile-menu-label">Table type</label> -->
            <v-select
              v-model="localTableType"
              :items="tableTypes"
              :menu-props="selectMenuProps"
              variant="outlined"
              density="compact"
              hide-details
            />
          </div>
          <v-btn size="small" variant="outlined" class="ocr-toolbar-btn" :loading="applyingType" @click="applyType">Apply type</v-btn>
          <v-btn size="small" color="primary" variant="flat" class="ocr-toolbar-btn" :loading="predicting" @click="runPredict">
            Predict
          </v-btn>
          <v-btn size="small" color="primary" variant="tonal" class="ocr-toolbar-btn" :loading="saving" @click="saveMapped">
            Save mapped
          </v-btn>
        </div>
      </div>

      <div
        class="ocr-report-map-workspace"
        :class="{
          'ocr-report-map-workspace--split': viewReportPage,
          'ocr-report-map-workspace--manual': viewReportPage && manualSelect,
        }"
      >
        <div v-if="viewReportPage" class="ocr-report-map-preview-col">
          <OcrReportPagePreview
            v-model:page-index="localPageIndex"
            :filename="filename"
            :max-page="maxPageIndex"
            :table-index="pageInverted ? null : tableIndex"
            :highlight-tables="!manualSelect && !pageInverted"
            :manual="manualSelect"
            :image-revision="pageImageRevision"
            class="ocr-report-map-page-preview"
            @boxes="manualBoxes = $event"
          />
          <div v-if="manualSelect" class="ocr-manual-select">
            <div class="ocr-manual-select-options">
              <v-checkbox v-model="specifyColumns" density="compact" hide-details label="Specify Number of Columns" />
              <v-text-field
                v-model.number="numColumns"
                type="number"
                min="1"
                max="20"
                density="compact"
                variant="outlined"
                hide-details
                label="Number of Columns"
                :disabled="!specifyColumns"
              />
              <v-checkbox v-model="specifyRows" density="compact" hide-details label="Specify Row Adjustment" />
              <v-text-field
                v-model.number="rowAdjustment"
                type="number"
                min="-20"
                max="20"
                density="compact"
                variant="outlined"
                hide-details
                label="Row Adjustment"
                title="Adjust the number of rows detected in the table. Positive values increase the row count, negative values decrease it."
                :disabled="!specifyRows"
              />
            </div>
            <v-btn
              size="small"
              variant="outlined"
              class="ocr-toolbar-btn"
              :loading="processingArea"
              :disabled="!manualBoxes.length"
              @click="processSelectedArea"
            >
              Process Selected Area as Table
            </v-btn>
            <template v-if="manualGrid">
              <OcrDataTable title="Selected area" :rows="manualRows" />
              <div class="ocr-manual-select-actions">
                <v-btn size="small" color="primary" variant="flat" class="ocr-toolbar-btn" :loading="replacingTable" @click="replaceManualTable">
                  Replace Table
                </v-btn>
                <v-btn size="small" variant="outlined" class="ocr-toolbar-btn" @click="cancelManualTable">Cancel</v-btn>
              </div>
            </template>
          </div>
        </div>
        <div class="ocr-report-map-workspace-main">
          <v-alert v-if="ignored" type="info" variant="tonal" density="compact">
            This table is ignored, so it stays out of mapping until Ignore table is unchecked on Report Tables.
          </v-alert>
          <OcrMappingStepper
            v-else
            v-model="stepIndex"
            :steps="steps"
            :saving="saving"
            @save-finish="saveAndFinish"
          />
          <v-alert
            v-if="showSavedEditWarning && !ignored"
            type="warning"
            variant="tonal"
            density="compact"
            class="ocr-map-saved-warning"
          >
            Editing a previously saved table may overwrite saved data. Use Save &amp; finish on Review when done.
          </v-alert>
          <div v-if="loading" class="ocr-empty-cell">Loading table…</div>
          <template v-else-if="detail && !ignored">
            <div v-if="currentStepId === 'edit'" class="ocr-map-edit">
              <OcrEditableGrid
                ref="editor"
                :grid="editGrid"
                :saving="savingEdits"
                :ignored="false"
                @save="saveEditedGrid"
                @reset="resetTable"
                @split="splitTable"
                @duplicate-table="duplicateTable"
                @update:ignored="setIgnored"
                @selection="gridSelection = $event"
              />
              <aside class="ocr-panel ocr-map-table-functions">
                <div class="ocr-panel-title">Table Functions</div>
                <v-checkbox
                  v-model="typeSpecific"
                  density="compact"
                  hide-details
                  label="Perform type-specific processing"
                />
                <p class="ocr-upload-hint">Run test type-specific rules when a template is matched or created.</p>
                <v-btn size="small" variant="outlined" class="ocr-toolbar-btn" @click="resetTable">
                  Reset Table
                </v-btn>
                <v-btn size="small" variant="outlined" class="ocr-toolbar-btn" :loading="matching" @click="matchTemplates">
                  Match Templates
                </v-btn>
                <p v-if="compositionHint" class="ocr-upload-hint">{{ compositionHint }}</p>
                <v-btn
                  size="small"
                  variant="outlined"
                  class="ocr-toolbar-btn"
                  :disabled="!canCreateTemplate"
                  :loading="creatingTemplate"
                  @click="createTemplate"
                >
                  Create Template
                </v-btn>
                <v-btn
                  size="small"
                  variant="outlined"
                  class="ocr-toolbar-btn"
                  :disabled="!canAddStopText"
                  :loading="addingStopText"
                  @click="addStopText"
                >
                  Add stop text
                </v-btn>
                <p v-if="canAddStopText" class="ocr-upload-hint">
                  The values in the selected rows and columns are added as stop text.
                </p>
              </aside>
            </div>
            <OcrReportColumnMap
              v-else-if="currentStepId === 'columns'"
              :filename="filename"
              :table-index="tableIndex"
              :source-grid="editGrid"
              :table-type="detail.table_type || ''"
              :list-revision="columnListRevision"
              @updated="onColumnMapUpdated"
              @status="onColumnMapStatus"
            />
            <OcrReportHeaderMap
              v-else-if="currentStepId === 'header'"
              :filename="filename"
              :table-index="tableIndex"
              :table-type="detail.table_type || ''"
              :list-revision="columnListRevision"
              @updated="onColumnMapUpdated"
              @status="onColumnMapStatus"
            />
            <div v-else-if="currentStepId === 'review'" class="ocr-map-review-step">
              <OcrReportValidationPanel
                ref="validationPanel"
                :filename="filename"
                :table-index="tableIndex"
                @updated="onReviewUpdated"
                @status="onColumnMapStatus"
              />
              <div class="ocr-report-review-head">
                <div>
                  <h2 class="ocr-report-review-type">{{ reviewTableType }}</h2>
                  <p class="ocr-report-review-status">status: {{ reviewStatus }}</p>
                </div>
              </div>
              <div v-if="reviewLoading" class="ocr-empty-cell">Loading review…</div>
              <template v-else>
                <div v-if="reviewHeaders.length" class="ocr-report-review-keys">
                  <p v-for="item in reviewHeaders" :key="item.field">
                    <strong>{{ item.field }}:</strong>
                    {{ item.value || "—" }}
                    <span v-if="item.uom"> [{{ item.uom }}]</span>
                  </p>
                </div>
                <div class="ocr-map-review-preview">
                  <OcrDataTable :rows="reviewRows" />
                </div>
              </template>
            </div>
            <div v-else-if="currentStepId === 'saved'" class="ocr-map-saved-step">
              <OcrDataTable v-if="savedRows.length" title="Saved table data" :rows="savedRows" />
              <p v-else class="ocr-empty-cell">
                No saved snapshot yet. Run validation on Review, then Save &amp; finish or Save mapped.
              </p>
            </div>
          </template>
        </div>
      </div>
    </template>
    <v-dialog v-model="invertOpen" max-width="760">
      <v-card class="ocr-dialog-card">
        <v-card-title class="ocr-dialog-title">
          <span>Invert Page</span>
          <v-btn class="ocr-dialog-close" icon="mdi-close" variant="text" aria-label="Close" @click="invertOpen = false" />
        </v-card-title>
        <v-card-text class="ocr-dialog-body">
          <p class="ocr-dialog-lead">Invert the selected page image and rerun OCR. Preview displayed below.</p>
          <img v-if="invertPreviewUrl" :src="invertPreviewUrl" class="ocr-invert-preview" alt="" :style="invertPreviewStyle" />
          <v-switch v-model="invertMirror" density="compact" hide-details color="primary" label="Mirror Image" />
        </v-card-text>
        <v-card-actions class="ocr-dialog-actions">
          <v-spacer />
          <v-btn variant="text" class="ocr-toolbar-btn" @click="invertOpen = false">Cancel</v-btn>
          <v-btn color="primary" variant="flat" class="ocr-toolbar-btn" :loading="inverting" @click="reprocessInverted">
            Reprocess Page
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ocrApi, { formatOcrApiError } from "@/services/ocrApi";
import OcrDataTable from "@/components/layout/OCR/OcrDataTable.vue";
import OcrEditableGrid from "@/components/layout/OCR/OcrEditableGrid.vue";
import OcrMappingStepper from "@/components/layout/OCR/OcrMappingStepper.vue";
import OcrReportColumnMap from "@/components/layout/OCR/OcrReportColumnMap.vue";
import OcrReportHeaderMap from "@/components/layout/OCR/OcrReportHeaderMap.vue";
import OcrReportPagePreview from "@/components/layout/OCR/OcrReportPagePreview.vue";
import OcrReportValidationPanel from "@/components/layout/OCR/OcrReportValidationPanel.vue";
import { ocrNotifyMixin } from "@/components/layout/OCR/OcrSnackbar.vue";

export default {
  name: "OcrReportMapPanel",
  mixins: [ocrNotifyMixin],
  components: {
    OcrDataTable,
    OcrEditableGrid,
    OcrMappingStepper,
    OcrReportColumnMap,
    OcrReportHeaderMap,
    OcrReportPagePreview,
    OcrReportValidationPanel,
  },
  props: {
    filename: { type: String, default: null },
    tableIndex: { type: Number, default: null },
    tableTypes: { type: Array, default: () => [] },
    maxPageIndex: { type: Number, default: 0 },
    pageIndex: { type: Number, default: 0 },
    refreshToken: { type: Number, default: 0 },
    startStep: { type: String, default: "" },
  },
  emits: ["updated", "update:pageIndex"],
  data() {
    return {
      loading: false,
      detail: null,
      localTableType: "",
      stepIndex: 0,
      viewReportPage: true,
      localPageIndex: 0,
      predicting: false,
      applyingType: false,
      saving: false,
      savingEdits: false,
      matching: false,
      creatingTemplate: false,
      addingStopText: false,
      typeSpecific: true,
      gridSelection: { rows: [], columns: [] },
      statusMessage: "",
      steps: [
        { id: "edit", label: "Edit" },
        { id: "columns", label: "Map Columns" },
        { id: "header", label: "Map Header" },
        { id: "review", label: "Review" },
        { id: "saved", label: "Saved" },
      ],
      statusType: "info",
      columnListRevision: 0,
      selectMenuProps: { contentClass: "ocr-select-menu" },
      manualSelect: false,
      manualBoxes: [],
      specifyColumns: false,
      numColumns: 2,
      specifyRows: false,
      rowAdjustment: 0,
      processingArea: false,
      replacingTable: false,
      manualGrid: null,
      invertOpen: false,
      invertMirror: false,
      invertPreviewUrl: "",
      inverting: false,
      resettingOrientation: false,
      pageImageRevision: 0,
      pageInverted: false,
      invertPending: false,
      review: null,
      reviewLoading: false,
      reviewRequest: 0,
    };
  },
  computed: {
    currentStepId() {
      return this.steps[this.stepIndex]?.id || "edit";
    },
    ignored() {
      return this.detail?.table_status === "Ignore";
    },
    showSavedEditWarning() {
      const st = this.detail?.table_status;
      return st === "Saved" || st === "Published";
    },
    editGrid() {
      if (!this.detail) return null;
      return this.detail.edited || this.detail.raw || { columns: [], data: [] };
    },
    mappedRows() {
      return this.gridToRows(this.detail?.mapped);
    },
    reviewTableType() {
      return this.review?.table_type || this.detail?.table_type || "Unknown";
    },
    reviewStatus() {
      return this.review?.table_status || this.detail?.table_status || "Not saved";
    },
    reviewHeaders() {
      return this.review?.original?.header || [];
    },
    reviewRows() {
      const mapped = this.gridToRows(this.review?.original?.grid);
      return mapped.length ? mapped : this.mappedRows;
    },
    savedRows() {
      return this.gridToRows(this.detail?.saved);
    },
    canCreateTemplate() {
      return this.gridSelection.rows.length > 0 && this.gridSelection.columns.length > 0;
    },
    canAddStopText() {
      return this.canCreateTemplate;
    },
    manualRows() {
      return this.gridToRows(this.manualGrid);
    },
    invertPreviewStyle() {
      const transform = this.invertMirror ? "rotate(180deg) scaleX(-1)" : "rotate(180deg)";
      return { transform };
    },
    compositionHint() {
      const type = this.localTableType || this.detail?.table_type || "";
      if (!["DLComposition", "SeparatorComposition", "CVDComposition"].includes(type)) return "";
      return "Every selected row becomes a column in the output, as does every selected column except the first.";
    },
  },
  watch: {
    filename: { handler() { this.review = null; this.loadDetail(true); } },
    tableIndex: { handler() { this.review = null; this.loadDetail(true); } },
    refreshToken() { this.loadDetail(false); },
    startStep() {
      this.applyStartStep();
    },
    pageIndex: {
      immediate: true,
      handler(v) {
        this.localPageIndex = v ?? 0;
      },
    },
    localPageIndex(v) {
      this.pageInverted = false;
      this.$emit("update:pageIndex", v);
      if (this.invertOpen) this.loadInvertPreview();
    },
    viewReportPage(on) {
      if (!on) this.manualSelect = false;
    },
    manualSelect(on) {
      if (!on) this.cancelManualTable();
    },
    statusMessage(message) {
      if (!message) return;
      this.$nextTick(() => {
        if (this.statusMessage) this.ocrToast(this.statusMessage, this.statusType);
      });
    },
    stepIndex(v) {
      if (v === 3) {
        this.loadReview();
        this.$nextTick(() => {
          this.$refs.validationPanel?.runValidation?.();
        });
      }
    },
  },
  mounted() {
    this.loadDetail(false);
  },
  beforeUnmount() {
    this.revokeInvertPreview();
  },
  methods: {
    gridToRows(grid) {
      if (!grid) return [];
      if (Array.isArray(grid)) return grid;
      if (grid.columns && grid.data) {
        return grid.data.map((row) => {
          const obj = {};
          grid.columns.forEach((col, i) => {
            obj[col] = row[i];
          });
          return obj;
        });
      }
      return [];
    },
    onColumnMapUpdated(table) {
      if (table) this.detail = { ...this.detail, ...table };
      this.$emit("updated");
    },
    onReviewUpdated(table) {
      this.onColumnMapUpdated(table);
      this.loadReview();
    },
    async loadReview() {
      if (this.currentStepId !== "review" || !this.filename || this.tableIndex == null || this.ignored) return;
      const request = this.reviewRequest + 1;
      this.reviewRequest = request;
      if (!this.review) this.reviewLoading = true;
      try {
        const { data } = await ocrApi.get(
          `api/v1/reports/${encodeURIComponent(this.filename)}/tables/${this.tableIndex}/review`,
        );
        if (this.reviewRequest !== request) return;
        this.review = data || null;
      } catch (e) {
        if (this.reviewRequest !== request) return;
        this.statusMessage = formatOcrApiError(e, "Could not load the review.");
        this.statusType = "error";
      } finally {
        if (this.reviewRequest === request) this.reviewLoading = false;
      }
    },
    onColumnMapStatus({ type, message }) {
      this.statusMessage = message;
      this.statusType = type;
    },
    applyStartStep() {
      if (!this.startStep) return false;
      const index = this.steps.findIndex((step) => step.id === this.startStep);
      if (index < 0) return false;
      this.stepIndex = index;
      return true;
    },
    async loadDetail(resetStep = false) {
      this.statusMessage = "";
      if (!this.filename || this.tableIndex == null) {
        this.detail = null;
        this.loading = false;
        return;
      }
      const refreshInPlace = !resetStep && !!this.detail;
      if (!refreshInPlace) {
        this.detail = null;
        this.loading = true;
      }
      try {
        const { data } = await ocrApi.get(
          `api/v1/reports/${encodeURIComponent(this.filename)}/tables/${this.tableIndex}`,
        );
        this.detail = data;
        this.localTableType = data.table_type || "";
        if (data.page != null) {
          this.localPageIndex = data.page;
        }
        if (resetStep) {
          if (!this.applyStartStep()) {
            const st = data.table_status;
            this.stepIndex = st === "Saved" || st === "Published" ? 4 : 0;
          }
        }
      } catch (e) {
        this.statusMessage = formatOcrApiError(e, "Could not load table.");
        this.statusType = "error";
      } finally {
        this.loading = false;
      }
    },
    editorPayload() {
      const editor = this.$refs.editor;
      const grid = editor?.currentGrid?.() || this.editGrid;
      const rows = editor?.selectedRows || this.gridSelection.rows || [];
      const columns = editor
        ? (editor.selectedCols || []).map((idx) => editor.localColumns[idx]).filter((name) => name != null && name !== "")
        : this.gridSelection.columns || [];
      return {
        type_specific_processing: this.typeSpecific,
        table_type: this.localTableType || "",
        grid,
        rows,
        columns,
      };
    },
    applyTemplateResult(data) {
      if (data?.table) {
        this.detail = data.table;
        this.localTableType = data.table.table_type || this.localTableType;
      }
      this.statusMessage = data?.message || "";
      this.statusType = data?.matched === false ? "info" : "success";
      this.gridSelection = { rows: [], columns: [] };
      this.$emit("updated");
    },
    async matchTemplates() {
      this.matching = true;
      this.statusMessage = "";
      try {
        const { data } = await ocrApi.post(this.tableUrl("/templates/match"), this.editorPayload());
        this.applyTemplateResult(data);
      } catch (e) {
        this.statusMessage = formatOcrApiError(e, "Template matching failed.");
        this.statusType = "error";
      } finally {
        this.matching = false;
      }
    },
    async createTemplate() {
      this.creatingTemplate = true;
      this.statusMessage = "";
      try {
        const { data } = await ocrApi.post(this.tableUrl("/templates"), this.editorPayload());
        this.applyTemplateResult(data);
      } catch (e) {
        this.statusMessage = formatOcrApiError(e, "Could not create a template.");
        this.statusType = "error";
      } finally {
        this.creatingTemplate = false;
      }
    },
    async addStopText() {
      this.addingStopText = true;
      this.statusMessage = "";
      try {
        const { data } = await ocrApi.post(this.tableUrl("/stop-text"), this.editorPayload());
        if (data?.table) this.detail = data.table;
        this.statusMessage = data?.message || "Stop text added.";
        this.statusType = "success";
        this.$emit("updated");
      } catch (e) {
        this.statusMessage = formatOcrApiError(e, "Could not add stop text.");
        this.statusType = "error";
      } finally {
        this.addingStopText = false;
      }
    },
    tableUrl(suffix = "") {
      return `api/v1/reports/${encodeURIComponent(this.filename)}/tables/${this.tableIndex}${suffix}`;
    },
    async saveEditedGrid(grid) {
      this.savingEdits = true;
      this.statusMessage = "";
      try {
        const { data } = await ocrApi.put(this.tableUrl(), { grid });
        this.detail = data.table;
        this.statusMessage = "Edited table saved.";
        this.statusType = "success";
        this.$emit("updated");
      } catch (e) {
        this.statusMessage = formatOcrApiError(e, "Could not save edits.");
        this.statusType = "error";
      } finally {
        this.savingEdits = false;
      }
    },
    revokeInvertPreview() {
      if (this.invertPreviewUrl) {
        URL.revokeObjectURL(this.invertPreviewUrl);
        this.invertPreviewUrl = "";
      }
    },
    async loadInvertPreview() {
      this.revokeInvertPreview();
      if (!this.filename) return;
      try {
        const { data } = await ocrApi.get(
          `api/v1/reports/${encodeURIComponent(this.filename)}/pages/${this.localPageIndex}/preview`,
          { responseType: "blob", params: { highlight: "false", rev: this.pageImageRevision || Date.now() } },
        );
        this.invertPreviewUrl = URL.createObjectURL(data);
      } catch (e) {
        this.statusMessage = formatOcrApiError(e, "Could not load the page image.");
        this.statusType = "error";
      }
    },
    openInvert() {
      this.invertOpen = true;
      this.loadInvertPreview();
    },
    async reprocessInverted() {
      if (!this.filename) return;
      this.inverting = true;
      this.statusMessage = "";
      try {
        const { data } = await ocrApi.post(
          `api/v1/reports/${encodeURIComponent(this.filename)}/pages/${this.localPageIndex}/invert`,
          { mirror: this.invertMirror },
        );
        this.manualGrid = data.grid || null;
        this.invertPending = true;
        this.pageInverted = true;
        this.pageImageRevision += 1;
        this.statusMessage = data.message || "";
        this.statusType = data.grid ? "success" : "warning";
        this.invertOpen = false;
        this.$emit("updated");
      } catch (e) {
        this.statusMessage = formatOcrApiError(e, "Could not reprocess the page.");
        this.statusType = "error";
      } finally {
        this.inverting = false;
      }
    },
    async resetOrientation() {
      if (!this.filename) return;
      this.resettingOrientation = true;
      try {
        await ocrApi.post(
          `api/v1/reports/${encodeURIComponent(this.filename)}/pages/${this.localPageIndex}/reset-orientation`,
        );
        if (this.invertPending) {
          this.manualGrid = null;
          this.invertPending = false;
        }
        this.pageInverted = false;
        this.pageImageRevision += 1;
        this.statusMessage = "Page orientation reset.";
        this.statusType = "success";
      } catch (e) {
        this.statusMessage = formatOcrApiError(e, "Could not reset the page orientation.");
        this.statusType = "error";
      } finally {
        this.resettingOrientation = false;
      }
    },
    async processSelectedArea() {
      if (!this.manualBoxes.length) {
        this.statusMessage = "Draw a box around the table on the page image.";
        this.statusType = "warning";
        return;
      }
      this.processingArea = true;
      this.statusMessage = "";
      try {
        const { data } = await ocrApi.post(this.tableUrl("/manual-selection"), {
          page_index: this.localPageIndex,
          boxes: this.manualBoxes,
          num_columns: this.specifyColumns ? Number(this.numColumns) : null,
          row_adjustment: this.specifyRows ? Number(this.rowAdjustment) : 0,
        });
        this.manualGrid = data.grid || null;
        this.statusMessage = data.message || "";
        this.statusType = data.grid ? "success" : "warning";
      } catch (e) {
        this.manualGrid = null;
        this.statusMessage = formatOcrApiError(e, "Could not read the selected area.");
        this.statusType = "error";
      } finally {
        this.processingArea = false;
      }
    },
    cancelManualTable() {
      this.manualGrid = null;
    },
    async replaceManualTable() {
      if (!this.manualGrid) return;
      this.replacingTable = true;
      try {
        const { data } = await ocrApi.post(this.tableUrl("/manual-selection/replace"), { grid: this.manualGrid });
        this.detail = data.table;
        this.manualGrid = null;
        this.statusMessage = "Table replaced from the selected area.";
        this.statusType = "success";
        this.$emit("updated");
      } catch (e) {
        this.statusMessage = formatOcrApiError(e, "Could not replace the table.");
        this.statusType = "error";
      } finally {
        this.replacingTable = false;
      }
    },
    async resetTable() {
      try {
        const { data } = await ocrApi.post(this.tableUrl("/reset"));
        this.detail = data.table;
        this.statusMessage = "Table reset to the original extraction.";
        this.statusType = "success";
        this.$emit("updated");
      } catch (e) {
        this.statusMessage = formatOcrApiError(e, "Could not reset the table.");
        this.statusType = "error";
      }
    },
    async splitTable(rows) {
      try {
        await ocrApi.post(this.tableUrl("/split"), { rows });
        this.statusMessage = "Selected rows moved into a new table.";
        this.statusType = "success";
        this.$emit("updated");
      } catch (e) {
        this.statusMessage = formatOcrApiError(e, "Could not split the table.");
        this.statusType = "error";
      }
    },
    async duplicateTable() {
      try {
        await ocrApi.post(this.tableUrl("/duplicate"));
        this.statusMessage = "Table duplicated.";
        this.statusType = "success";
        this.$emit("updated");
      } catch (e) {
        this.statusMessage = formatOcrApiError(e, "Could not duplicate the table.");
        this.statusType = "error";
      }
    },
    async setIgnored(ignored) {
      try {
        const { data } = await ocrApi.post(this.tableUrl("/ignore"), { ignored: !!ignored });
        this.detail = data.table;
        this.statusMessage = ignored
          ? "Table ignored. It stays out of mapping until this is unchecked on Report Tables."
          : "Table included in mapping again.";
        this.statusType = "success";
        this.$emit("updated");
      } catch (e) {
        this.statusMessage = formatOcrApiError(e, "Could not update ignore.");
        this.statusType = "error";
      }
    },
    async runPredict() {
      this.predicting = true;
      this.statusMessage = "";
      try {
        const { data } = await ocrApi.post(
          `api/v1/reports/${encodeURIComponent(this.filename)}/tables/${this.tableIndex}/predict`,
        );
        this.detail = data.table;
        this.localTableType = data.table?.table_type || "";
        this.columnListRevision += 1;
        this.statusMessage = data.matched ? "Table type predicted." : "Prediction completed (review steps).";
        this.statusType = data.matched ? "success" : "warning";
        this.$emit("updated");
      } catch (e) {
        this.statusMessage = formatOcrApiError(e, "Predict failed.");
        this.statusType = "error";
      } finally {
        this.predicting = false;
      }
    },
    async applyType() {
      if (!this.localTableType) return;
      this.applyingType = true;
      try {
        const { data } = await ocrApi.put(
          `api/v1/reports/${encodeURIComponent(this.filename)}/tables/${this.tableIndex}/type`,
          { table_type: this.localTableType },
        );
        this.detail = data;
        this.columnListRevision += 1;
        this.statusMessage = "Table type updated.";
        this.statusType = "success";
        this.$emit("updated");
      } catch (e) {
        this.statusMessage = formatOcrApiError(e, "Could not set table type.");
        this.statusType = "error";
      } finally {
        this.applyingType = false;
      }
    },
    async saveMapped() {
      this.saving = true;
      try {
        const { data } = await ocrApi.post(
          `api/v1/reports/${encodeURIComponent(this.filename)}/tables/${this.tableIndex}/save-mapped`,
        );
        this.detail = data.table;
        this.statusMessage = data.message || (data.ok ? "Saved." : "Save failed.");
        this.statusType = data.ok ? "success" : "warning";
        if (data.ok) this.stepIndex = 4;
        this.$emit("updated");
      } catch (e) {
        this.statusMessage = formatOcrApiError(e, "Save failed.");
        this.statusType = "error";
      } finally {
        this.saving = false;
      }
    },
    async saveAndFinish() {
      await this.saveMapped();
      if (this.statusType === "success" || this.statusType === "warning") {
        this.stepIndex = 4;
      }
    },
  },
};
</script>
